import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import Button from '../../../atoms/Button/Button';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import Home from '../../../atoms/Icon/Home';
import Bell from '../../../atoms/Icon/Bell';
import Menu from '../../../atoms/Icon/Menu';
import Stand from '../../../atoms/Icon/Stand';
import DotsGrid from '../../../atoms/Icon/DotsGrid';
import ToolPanelPopover from '../../../molecules/ToolPanelPopover/ToolPanelPopover';
import { AgenticTopBarProps, BreadcrumbItem, DEFAULT_BREADCRUMBS } from '../AskAsperAgentic.types';

/**
 * AgenticTopBar - Top navigation bar with breadcrumb, actions, and notification
 * Integrates functionality from AskAsperHeader component
 * Part of the AskAsper Agentic Platform components
 */
const AgenticTopBar: React.FC<AgenticTopBarProps> = ({
    breadcrumbs = DEFAULT_BREADCRUMBS,
    showNotification = true,
    notificationCount = 0,
    onNotificationClick,
    onBreadcrumbClick,
    onHomeClick,
    // Menu toggle
    onMenuToggle,
    // View mode
    viewMode = 'list',
    onViewModeChange,
    showViewModeToggle = true,
    // Action buttons
    onCommentClick,
    onCreateCaseClick,
    showCommentButton = true,
    showCreateCaseButton = true,
    // DotsGrid / Tool Panel
    onDotsGridClick,
    showDotsGridButton = true,
    toolPanelOpen: controlledToolPanelOpen,
    onToolPanelOpenChange,
    onExportClick,
    onShareClick,
    onPlaceholderClick,
    onToolPanelSearch,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Internal state for uncontrolled tool panel mode
    const [internalToolPanelOpen, setInternalToolPanelOpen] = useState(false);

    // Use controlled or uncontrolled state
    const toolPanelOpen = controlledToolPanelOpen !== undefined ? controlledToolPanelOpen : internalToolPanelOpen;

    const handleToolPanelOpenChange = (open: boolean) => {
        if (controlledToolPanelOpen === undefined) {
            setInternalToolPanelOpen(open);
        }
        onToolPanelOpenChange?.(open);

        // Also call the legacy onDotsGridClick when opening
        if (open && onDotsGridClick) {
            onDotsGridClick();
        }
    };

    const handleBreadcrumbClick = (item: BreadcrumbItem, index: number) => {
        if (onBreadcrumbClick && item.href) {
            onBreadcrumbClick(item, index);
        }
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between p-4 w-full"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-top-bar
            >
                {/* Left side - Menu toggle and Breadcrumbs */}
                <div className="flex items-center gap-4">
                    {/* Menu Toggle Button - Always visible fixed component */}
                    <Tooltip title="View all threads" placement="bottom" getPopupContainer={() => document.body}>
                        <Button
                            type="text"
                            className="flex items-center justify-center w-8 h-8 p-0"
                            onClick={onMenuToggle || (() => {})}
                            icon={<Menu size={20} color="rgba(0,0,0,0.65)" />}
                            aria-label="View all threads"
                        />
                    </Tooltip>

                    {/* Breadcrumb Navigation */}
                    <nav className="flex items-center flex-wrap gap-0" aria-label="Breadcrumb">
                        {breadcrumbs.map((item, index) => (
                            <React.Fragment key={index}>
                                {/* Breadcrumb Item */}
                                <div
                                    className={`flex items-center gap-1 ${
                                        item.href ? 'cursor-pointer hover:opacity-80' : ''
                                    }`}
                                    onClick={() => handleBreadcrumbClick(item, index)}
                                >
                                    {index === 0 && (
                                        <div
                                            className="py-1 cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.location.href = '/';
                                            }}
                                        >
                                            <Home size={20} color="rgba(0,0,0,0.45)" />
                                        </div>
                                    )}
                                    {index > 0 && (
                                        <span
                                            className={`font-['Open_Sans'] font-normal text-sm leading-[22px] ${
                                                index === breadcrumbs.length - 1
                                                    ? "font-['Open_Sans'] font-semibold text-[#18181b]"
                                                    : 'text-[rgba(0,0,0,0.88)]'
                                            }`}
                                        >
                                            {item.label}
                                        </span>
                                    )}
                                </div>

                                {/* Separator */}
                                {index < breadcrumbs.length - 1 && (
                                    <span className="px-2 font-['Open_Sans'] font-normal text-sm leading-[22px] text-[rgba(0,0,0,0.45)]">
                                        /
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>
                </div>

                {/* Right side - Action buttons */}
                <div className="flex items-center gap-3">
                    {/* View Mode Toggle */}
                    {showViewModeToggle && (
                        <div className="bg-[#f4f4f5] flex items-center p-0.5 rounded-md">
                            <div
                                className={`
                                    flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all
                                    ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}
                                `}
                                onClick={() => onViewModeChange?.('list')}
                                title="List View"
                            >
                                <Menu size={20} color={viewMode === 'list' ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.45)'} />
                            </div>
                            <div
                                className={`
                                    flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all
                                    ${viewMode === 'presentation' ? 'bg-white shadow-sm' : ''}
                                `}
                                onClick={() => onViewModeChange?.('presentation')}
                                title="Presentation View"
                            >
                                <Stand
                                    size={20}
                                    color={viewMode === 'presentation' ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.45)'}
                                />
                            </div>
                        </div>
                    )}


                    {/* Create Case Button */}
                    {showCreateCaseButton && (
                        <Button
                            type="primary"
                            onClick={onCreateCaseClick}
                            className="h-8 bg-[#1f1f1f] border-[#1f1f1f] hover:bg-[#333] hover:border-[#333]"
                        >
                            <div className="flex items-center gap-2">
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M20 7H4C3.44772 7 3 7.44772 3 8V19C3 19.5523 3.44772 20 4 20H20C20.5523 20 21 19.5523 21 19V8C21 7.44772 20.5523 7 20 7Z"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M16 7V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V7"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="text-white">Create Case</span>
                            </div>
                        </Button>
                    )}

                    {/* Dots Grid Button with Tool Panel Popover */}
                    {showDotsGridButton && (
                        <ToolPanelPopover
                            open={toolPanelOpen}
                            onOpenChange={handleToolPanelOpenChange}
                            onExportClick={onExportClick}
                            onShareClick={onShareClick}
                            onPlaceholderClick={onPlaceholderClick}
                            onSearch={onToolPanelSearch}
                            data-test-id={`${dataTestId}-tool-panel`}
                        >
                            <Button
                                type="default"
                                className="flex items-center justify-center w-8 h-8 p-0 bg-[#f7f5ff] border-[#bab1e2] hover:bg-[#efe9ff]"
                                icon={<DotsGrid size={20} color="#5f49bc" />}
                            />
                        </ToolPanelPopover>
                    )}

                    {/* Notification Bell */}
                    {showNotification && (
                        <div
                            className="bg-[#f7f5ff] flex items-center justify-center p-2 rounded-2xl size-8 cursor-pointer hover:bg-[#ede9ff] transition-colors relative"
                            onClick={onNotificationClick}
                            data-test-id={`${dataTestId}-notification`}
                        >
                            <Bell size={24} color="#5F49BC" />
                            {notificationCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-sm rounded-full size-4 flex items-center justify-center">
                                    {notificationCount > 9 ? '9+' : notificationCount}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticTopBar;
